import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FaHistory,
  FaCoins,
  FaClock,
  FaCheckCircle,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaSpinner,
} from "react-icons/fa";
import { useGetRedeemHistoryQuery } from "../../../redux/rtk/apiRtk";
import { primaryColor } from "../../../utils/constants";
import moment from "moment";

const RedeemHistory = () => {
  const [activeTab, setActiveTab] = useState("pending");
  const [pendingPage, setPendingPage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);
  const [pageSize] = useState(10);

  // Get current page based on active tab
  const getCurrentPage = () => {
    return activeTab === "pending" ? pendingPage : completedPage;
  };

  // API calls for both tabs with separate pagination
  const {
    data: pendingData,
    isLoading: pendingLoading,
    error: pendingError,
  } = useGetRedeemHistoryQuery({
    status: false,
    page: pendingPage,
    limit: pageSize,
  });

  const {
    data: completedData,
    isLoading: completedLoading,
    error: completedError,
  } = useGetRedeemHistoryQuery({
    status: true,
    page: completedPage,
    limit: pageSize,
  });

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Don't reset page when switching tabs - maintain separate pagination
  };

  const handlePageChange = (page) => {
    if (activeTab === "pending") {
      setPendingPage(page);
    } else {
      setCompletedPage(page);
    }
  };

  const getCurrentData = () => {
    return activeTab === "pending" ? pendingData : completedData;
  };

  const getCurrentLoading = () => {
    return activeTab === "pending" ? pendingLoading : completedLoading;
  };

  const getCurrentError = () => {
    return activeTab === "pending" ? pendingError : completedError;
  };

  const TransactionCard = ({ transaction }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 hover:shadow-md transition-all duration-300 w-full mb-4"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 sm:space-x-4">
          <div
            className={`p-2 sm:p-3 rounded-full flex-shrink-0 ${
              transaction.status ? "bg-green-100" : "bg-yellow-100"
            }`}
          >
            {transaction.status ? (
              <FaCheckCircle className="text-lg sm:text-xl text-green-600" />
            ) : (
              <FaClock className="text-lg sm:text-xl text-yellow-600" />
            )}
          </div>

          <div>
            <div className="flex items-center space-x-2 mb-1">
              <FaCoins className="text-yellow-500 text-xs sm:text-sm" />
              <span
                className="text-lg sm:text-2xl font-bold"
                style={{ color: primaryColor }}
              >
                ₹{transaction.amount}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 text-xs sm:text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <FaCalendarAlt className="text-[10px] sm:text-xs" />
                <span>
                  {moment(transaction.createdAt).format("MMM Do, YYYY")}
                </span>
              </div>
              <span className="hidden sm:inline">•</span>
              <span>{moment(transaction.createdAt).format("h:mm A")}</span>
            </div>
          </div>
        </div>

        <div className="text-right flex-shrink-0 ml-2">
          <div
            className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-medium ${
              transaction.status
                ? "bg-green-100 text-green-800"
                : "bg-yellow-100 text-yellow-800"
            }`}
          >
            {transaction.status ? "Completed" : "Pending"}
          </div>
          <div className="text-[10px] sm:text-xs text-gray-400 mt-1">
            ID: {transaction._id.slice(-8)}
          </div>
        </div>
      </div>
    </motion.div>
  );

  const Pagination = ({
    currentPage,
    totalPages,
    onPageChange,
    totalItems,
  }) => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    return (
      <div className="mt-8">
        {/* Pagination Info */}
        <div className="text-center mb-4">
          <p className="text-sm text-gray-600">
            Showing <span className="font-medium">{startItem}</span> to{" "}
            <span className="font-medium">{endItem}</span> of{" "}
            <span className="font-medium">{totalItems}</span> transactions
          </p>
        </div>

        {/* Pagination Controls */}
        <div className="flex flex-wrap items-center justify-center space-x-2 overflow-x-auto">
          {/* First Page */}
          {currentPage > 3 && totalPages > maxVisiblePages && (
            <>
              <button
                onClick={() => onPageChange(1)}
                className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all duration-200"
              >
                1
              </button>
              {currentPage > 4 && (
                <span className="px-2 text-gray-400">...</span>
              )}
            </>
          )}

          {/* Previous Button */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
          >
            Previous
          </button>

          {/* Page Numbers */}
          {pages.map((page) => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`px-3 py-2 rounded-lg transition-all duration-200 font-medium ${
                page === currentPage
                  ? "text-white shadow-lg transform scale-105"
                  : "border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300"
              }`}
              style={{
                backgroundColor:
                  page === currentPage ? primaryColor : "transparent",
              }}
            >
              {page}
            </button>
          ))}

          {/* Next Button */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
          >
            Next
          </button>

          {/* Last Page */}
          {currentPage < totalPages - 2 && totalPages > maxVisiblePages && (
            <>
              {currentPage < totalPages - 3 && (
                <span className="px-2 text-gray-400">...</span>
              )}
              <button
                onClick={() => onPageChange(totalPages)}
                className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all duration-200"
              >
                {totalPages}
              </button>
            </>
          )}
        </div>

        {/* Quick Jump */}
        {totalPages > 10 && (
          <div className="flex items-center justify-center mt-4 space-x-2">
            <span className="text-sm text-gray-600">Go to page:</span>
            <input
              type="number"
              min="1"
              max={totalPages}
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e.target.value);
                if (page >= 1 && page <= totalPages) {
                  onPageChange(page);
                }
              }}
              className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-opacity-50"
              style={{ focusRingColor: primaryColor }}
            />
            <span className="text-sm text-gray-600">of {totalPages}</span>
          </div>
        )}
      </div>
    );
  };

  const currentData = getCurrentData();
  const isLoading = getCurrentLoading();
  const error = getCurrentError();
  const totalPages = currentData ? Math.ceil(currentData.total / pageSize) : 1;
  const currentPage = getCurrentPage();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8  sm:px-6 lg:px-8">
      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="bg-white rounded-2xl shadow-lg p-2 mb-8"
        style={{ boxShadow: "0 10px 40px rgba(164, 1, 101, 0.1)" }}
      >
        <div className="flex space-x-2 overflow-x-auto">
          <button
            onClick={() => handleTabChange("pending")}
            className={`flex-1 flex items-center justify-center space-x-2 py-3 px-6 rounded-xl font-semibold transition-all duration-300 ${
              activeTab === "pending"
                ? "text-white shadow-lg"
                : "text-gray-600 hover:bg-gray-50"
            }`}
            style={{
              backgroundColor:
                activeTab === "pending" ? primaryColor : "transparent",
            }}
          >
            <FaClock className="text-sm" />
            <span>Pending Requests</span>
            {pendingData && (
              <span
                className={`px-2 py-1 rounded-full text-xs ${
                  activeTab === "pending"
                    ? "bg-white/20 text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {pendingData.total}
              </span>
            )}
          </button>

          <button
            onClick={() => handleTabChange("completed")}
            className={`flex-1 flex items-center justify-center space-x-2 py-3 px-6 rounded-xl font-semibold transition-all duration-300 ${
              activeTab === "completed"
                ? "text-white shadow-lg"
                : "text-gray-600 hover:bg-gray-50"
            }`}
            style={{
              backgroundColor:
                activeTab === "completed" ? primaryColor : "transparent",
            }}
          >
            <FaCheckCircle className="text-sm" />
            <span>Completed</span>
            {completedData && (
              <span
                className={`px-2 py-1 rounded-full text-xs ${
                  activeTab === "completed"
                    ? "bg-white/20 text-white"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                {completedData.total}
              </span>
            )}
          </button>
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-white rounded-2xl shadow-lg p-6"
        style={{ boxShadow: "0 10px 40px rgba(164, 1, 101, 0.1)" }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <FaSpinner
                className="text-4xl animate-spin mb-4"
                style={{ color: primaryColor }}
              />
              <p className="text-gray-600">Loading transactions...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Error Loading Data
            </h3>
            <p className="text-gray-600">Please try again later</p>
          </div>
        ) : currentData?.results?.length === 0 ? (
          <div className="text-center py-20">
            <FaMoneyBillWave className="text-6xl text-gray-300 mb-4 mx-auto" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No {activeTab} transactions
            </h3>
            <p className="text-gray-600">
              {activeTab === "pending"
                ? "You don't have any pending redeem requests"
                : "You haven't completed any transactions yet"}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {currentData?.results?.map((transaction) => (
                <TransactionCard
                  key={transaction._id}
                  transaction={transaction}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={currentData.total}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </motion.div>
    </div>
  );
};

export default RedeemHistory;
